import type {
  RevenueByModelResponse,
  RevenueByTenantResponse,
  RevenueDailyResponse,
  RevenueSummaryResponse,
  RevenueWindow,
} from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers + React Query hooks for the `/v1/ops/revenue`
 * surface (Epic #84 P11).
 *
 * All four endpoints are read-only GET, bearer-authed upstream via
 * the ops-api session cookie. Query-key shape keeps window + filter
 * args as part of the key so switching windows hits a fresh cache
 * slot without manual invalidation.
 *
 *   - summary: `['revenue', 'summary', window]`
 *   - daily:   `['revenue', 'daily', days]`
 *   - by-model: `['revenue', 'by-model']`
 *   - by-tenant: `['revenue', 'by-tenant', limit]`
 */

const BASE = '/v1/ops/revenue';

export const REVENUE_QUERY_KEYS = {
  summary: (window: RevenueWindow) => ['revenue', 'summary', window] as const,
  daily: (days: number) => ['revenue', 'daily', days] as const,
  byModel: () => ['revenue', 'by-model'] as const,
  byTenant: (limit: number) => ['revenue', 'by-tenant', limit] as const,
};

export async function getRevenueSummary(
  window: RevenueWindow,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueSummaryResponse> {
  const qs = new URLSearchParams({ window });
  return opsApiRequest<RevenueSummaryResponse>(
    `${BASE}/summary?${qs.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function getRevenueDaily(
  days: number,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueDailyResponse> {
  const qs = new URLSearchParams({ days: String(days) });
  return opsApiRequest<RevenueDailyResponse>(
    `${BASE}/daily?${qs.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function getRevenueByModel(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueByModelResponse> {
  return opsApiRequest<RevenueByModelResponse>(`${BASE}/by-model`, { signal }, fetchImpl);
}

export async function getRevenueByTenant(
  limit: number,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RevenueByTenantResponse> {
  const qs = new URLSearchParams({ limit: String(limit) });
  return opsApiRequest<RevenueByTenantResponse>(
    `${BASE}/by-tenant?${qs.toString()}`,
    { signal },
    fetchImpl,
  );
}

export function useRevenueSummary(
  window: RevenueWindow,
): UseQueryResult<RevenueSummaryResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.summary(window),
    queryFn: ({ signal }) => getRevenueSummary(window, fetchImpl, signal),
  });
}

export function useRevenueDaily(days: number): UseQueryResult<RevenueDailyResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.daily(days),
    queryFn: ({ signal }) => getRevenueDaily(days, fetchImpl, signal),
  });
}

export function useRevenueByModel(): UseQueryResult<RevenueByModelResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.byModel(),
    queryFn: ({ signal }) => getRevenueByModel(fetchImpl, signal),
  });
}

export function useRevenueByTenant(limit: number): UseQueryResult<RevenueByTenantResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: REVENUE_QUERY_KEYS.byTenant(limit),
    queryFn: ({ signal }) => getRevenueByTenant(limit, fetchImpl, signal),
  });
}
